import { captureException } from '@sentry/browser';
import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { AppThunk } from 'clients/app/src/store';
import { normalize } from 'normalizr';

import { DeleteQuestionEventAdapter } from '~analytics/adapters/DeleteQuestionEventAdapter';
import { QuestionSavedEventAdapter } from '~analytics/adapters/QuestionSavedEventAdapter';
import { quizzesQueryKey } from '~teacher/hook';
import { ValidationError } from '~teacher/models/errors';
import { Quiz, QuizQuestionSchema, QuizSchema } from '~teacher/models/quizzes';
import { queryClient } from '~teacher/queryClient';

import {
  QuizQuestionOrderUrl,
  QuizQuestionUrl,
} from '../../../api/question/questionApi';
import * as questionApi from '../../../api/question/questionApi';
import { DeleteQuizUrl, QuizUrl } from '../../../api/quizApi';
import * as quizApi from '../../../api/quizApi';
import { QuizValidation } from '../../validators/QuizValidation';
import {
  addQuestionToQuiz,
  beginEditingQuestion,
  changeStatusToError,
  changeStatusToLoading,
  changeStatusToOk,
  changeStatusToSaving,
  createBlankQuestion,
  finishEditingQuestion,
  finishEditingQuiz,
  removeQuestion,
  setQuizAnswers,
  setQuizQuestions,
  setQuizzes,
  updateQuizQuestionOrder,
} from './quizSlice';

const setQuestionAndAnswers =
  ({
    quizQuestion,
    quizAnswer,
  }: {
    quizQuestion: { [key: string]: any };
    quizAnswer: { [key: string]: any };
  }): AppThunk =>
  async (dispatch) => {
    dispatch(setQuizQuestions({ questions: quizQuestion }));
    Object.values(quizQuestion).forEach((quizQuestion) => {
      const { answers, type } = quizQuestion;
      const answersToSet = answers.reduce((aggregator, answerId) => {
        aggregator[answerId] = quizAnswer[answerId];
        return aggregator;
      }, {});
      dispatch(setQuizAnswers({ answers: answersToSet, questionType: type }));
    });
  };

const populateQuizData =
  (quizToPopulate: any): AppThunk =>
  async (dispatch, getstate, extraArguments) => {
    const {
      createdById,
      createdDate,
      id,
      parentId,
      version,
      sharable = false,
      lastUpdated,
      name,
      questions,
      socNumber,
      standard,
      deleted,
    } = quizToPopulate;

    const newQuiz: Quiz = {
      deleted,
      parentId,
      createdById,
      createdDate,
      id,
      version,
      isSharingEnabled: sharable,
      lastUpdated,
      name,
      questions,
      socNumber,
      standard,
    };
    const result = normalize(newQuiz, QuizSchema);
    const { quiz, quizQuestion = {}, quizAnswer = {} } = result.entities;

    dispatch(setQuizzes({ quiz: quiz, id: id }));
    setQuestionAndAnswers({ quizQuestion, quizAnswer })(
      dispatch,
      getstate,
      extraArguments
    );
  };

const createQuestion =
  ({
    quizQuestionUrl,
    unselectQuestion,
    selectQuestion,
    selectedQuestions,
  }: {
    quizQuestionUrl: QuizQuestionUrl;
    unselectQuestion: (questionId: string | number) => void;
    selectQuestion: (questionId: string | number) => void;
    selectedQuestions: Set<string>;
  }): AppThunk =>
  async (dispatch, getState, extraArguments) => {
    const state = getState();
    const {
      questionId: originalQuestionId,
      explanation,
      explanationImage,
      type,
      gradingWeight,
      order,
      questionText,
      questionImage,
      answers: answerIds,
      questionVideo,
      questionAudio,
    } = state.teachers.quizData.editQuestion;
    const { id } = state.teachers.quizData.editQuiz;

    const answers = answerIds
      .map((id) => state.teachers.quizData.editAnswers[id])
      .filter(Boolean); // Filter out undefined.

    state.teachers.quizData.editAnswers;
    const { question, orders } = await questionApi.createQuestion({
      answers: answers,
      explanation: explanation,
      explanationImage: explanationImage,
      gradingWeight: gradingWeight,
      order: order,
      questionAudio: questionAudio?.url,
      questionImage: questionImage,
      questionText: questionText,
      questionVideo: questionVideo,
      quizId: id,
      quizQuestionUrl: quizQuestionUrl,
      type: type,
    });
    const result = normalize(question, QuizQuestionSchema);
    const {
      entities: { quizQuestion: newQuestions, quizAnswer: newAnswers },
    } = result;

    setQuestionAndAnswers({
      quizQuestion: newQuestions,
      quizAnswer: newAnswers,
    })(dispatch, getState, extraArguments);

    dispatch(
      addQuestionToQuiz({ quizId: id, questionId: question.questionId })
    );
    dispatch(updateQuizQuestionOrder({ questionOrderMap: orders }));
    dispatch(finishEditingQuestion());

    // Update selected questions if required
    if (selectedQuestions.has(`${originalQuestionId}`)) {
      unselectQuestion(`${originalQuestionId}`);
      selectQuestion(`${question.questionId}`);
    }

    new QuestionSavedEventAdapter({
      originalQuestion: null,
      originalAnswers: null,
      modifiedQuestion: question,
      modifiedAnswers: Object.values(newAnswers ?? {}),
    }).sendAnalytics();
  };

const updateQuestion =
  (quizQuestionUrl: QuizQuestionUrl): AppThunk =>
  async (dispatch, getState, extraArguments) => {
    const state = getState();
    const {
      explanation,
      explanationImage,
      gradingWeight,
      questionId,
      questionText,
      questionImage,
      answers: answerIds,
      questionVideo,
      questionAudio,
    } = state.teachers.quizData.editQuestion;
    const { id } = state.teachers.quizData.editQuiz;

    const answersToSave = answerIds
      .map((id) => state.teachers.quizData.editAnswers[id])
      .filter(Boolean); // Filter out undefined

    const oldAnswers = answerIds
      .map((id) => state.teachers.quizData.answers[id])
      .filter(Boolean); // Filter out undefined

    const { question } = await questionApi.updateQuestion({
      answers: answersToSave,
      explanation: explanation,
      explanationImage: explanationImage,
      gradingWeight: gradingWeight,
      questionAudio: questionAudio?.url,
      questionId: questionId as number,
      questionImage: questionImage,
      questionText: questionText,
      questionVideo: questionVideo,
      quizId: id,
      quizQuestionUrl: quizQuestionUrl,
    });
    const result = normalize(question, QuizQuestionSchema);
    const {
      entities: { quizQuestion: newQuestions, quizAnswer: newAnswers },
    } = result;

    await setQuestionAndAnswers({
      quizQuestion: newQuestions,
      quizAnswer: newAnswers,
    })(dispatch, getState, extraArguments);

    dispatch(finishEditingQuestion());

    try {
      const originalQuestion =
        state.teachers.quizData.questions[`${questionId}`];
      new QuestionSavedEventAdapter({
        originalQuestion: originalQuestion,
        modifiedQuestion: question,
        originalAnswers: oldAnswers,
        modifiedAnswers: Object.values(newAnswers ?? {}),
      }).sendAnalytics();
    } catch (err) {
      console.error(err);
    }
  };

const saveQuestion =
  ({
    quizQuestionUrl,
    unselectQuestion,
    selectQuestion,
    selectedQuestions,
  }: {
    quizQuestionUrl: QuizQuestionUrl;
    selectedQuestions: Set<string>;
    unselectQuestion: (questionId: string | number) => void;
    selectQuestion: (questionId: string | number) => void;
  }): AppThunk =>
  async (dispatch, getState, extraArguments) => {
    const state = getState();
    const { editQuestion, editAnswers } = state.teachers.quizData;
    // The Question was edited, but nothing changed
    if (!editQuestion.isDirty) {
      unselectQuestion(`${editQuestion.questionId}`);
      dispatch(finishEditingQuestion());
      return;
    }

    dispatch(changeStatusToSaving());

    if (editQuestion) {
      const invalidations = new QuizValidation().validateQuestion(
        editQuestion,
        editAnswers
      );
      if (invalidations.length > 0) {
        dispatch(changeStatusToOk());
        throw new ValidationError(invalidations);
      }
    }

    const {
      teachers: {
        quizData: {
          editQuestion: { questionId },
        },
      },
    } = state;
    const isCreating = typeof questionId === 'string';
    if (isCreating) {
      // If we create a new question, we need the question id,
      await createQuestion({
        quizQuestionUrl: quizQuestionUrl,
        selectQuestion: selectQuestion,
        unselectQuestion: unselectQuestion,
        selectedQuestions: selectedQuestions,
      })(dispatch, getState, extraArguments);
    } else {
      await updateQuestion(quizQuestionUrl)(dispatch, getState, extraArguments);
    }
    dispatch(changeStatusToOk());
  };

const editQuestion =
  ({
    questionId,
    quizQuestionUrl,
    unselectQuestion,
    selectQuestion,
    selectedQuestions,
  }: {
    questionId: string;
    quizQuestionUrl: QuizQuestionUrl;
    unselectQuestion: (questionId: string) => void;
    selectQuestion: (questionId: string) => void;
    selectedQuestions: Set<string>;
  }): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const editQuestion = state.teachers.quizData.editQuestion;
    if (editQuestion) {
      await dispatch(
        saveQuestion({
          quizQuestionUrl: quizQuestionUrl,
          unselectQuestion: unselectQuestion,
          selectQuestion: selectQuestion,
          selectedQuestions: selectedQuestions,
        })
      );
    }
    dispatch(beginEditingQuestion({ questionId }));
  };

const saveQuiz =
  ({
    quizUrl,
    quizQuestionUrl,
    unselectQuestion,
    selectQuestion,
    selectedQuestions,
  }: {
    quizUrl: QuizUrl;
    quizQuestionUrl: QuizQuestionUrl;
    unselectQuestion: (questionId: string) => void;
    selectQuestion: (questionId: string) => void;
    selectedQuestions: Set<string>;
  }): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    dispatch(changeStatusToSaving());
    const editQuestion = state.teachers.quizData.editQuestion;
    if (editQuestion) {
      await dispatch(
        saveQuestion({
          quizQuestionUrl: quizQuestionUrl,
          unselectQuestion: unselectQuestion,
          selectQuestion: selectQuestion,
          selectedQuestions: selectedQuestions,
        })
      );
    }

    const editQuiz = state.teachers.quizData.editQuiz;

    const quizValidation = new QuizValidation().validateQuiz(editQuiz);
    if (!editQuiz.isDirty || quizValidation.skipSave) {
      dispatch(finishEditingQuiz());
      dispatch(changeStatusToOk());
      queryClient.invalidateQueries(quizzesQueryKey.quiz);
      return;
    }

    if (quizValidation.invalidations.length > 0) {
      dispatch(changeStatusToOk());
      throw new ValidationError(quizValidation.invalidations);
    }

    const { id, name, isSharingEnabled } = editQuiz;
    const updatedQuiz = await quizApi.updateQuiz({
      isSharable: isSharingEnabled,
      name: name,
      quizId: id,
      quizUrl: quizUrl,
    });

    dispatch(populateQuizData(updatedQuiz));
    dispatch(finishEditingQuiz());
    dispatch(changeStatusToOk());
    queryClient.invalidateQueries(quizzesQueryKey.quiz);
  };

/**
 * @deprecated This isn't actually called anywhere anymore :(
 */
const deleteQuestion =
  ({
    questionId,
    quizId,
    quizQuestionUrl,
  }: {
    questionId: number | string;
    quizId: number;
    quizQuestionUrl: QuizQuestionUrl;
  }): AppThunk =>
  async (dispatch, getState) => {
    const isWorkspaceQuizQuestion = !!quizQuestionUrl({
      questionId,
      quizId,
    })?.match(/workspace/);
    const state = getState();
    const {
      teachers: {
        quizData: { editQuestion },
      },
    } = state;
    dispatch(changeStatusToSaving());

    if (typeof questionId === 'number') {
      const result = await questionApi.deleteQuestion({
        questionId,
        quizId,
        quizQuestionUrl,
      });
      if (result && result.orders) {
        dispatch(updateQuizQuestionOrder({ questionOrderMap: result.orders }));
      }
      // Analytics
      new DeleteQuestionEventAdapter({
        source: isWorkspaceQuizQuestion ? 'shared_library' : 'personal_library',
        deleteType: 'single',
        questionsCount: 1,
      }).sendAnalytics();
    }
    dispatch(removeQuestion({ questionIds: [questionId] }));
    if (editQuestion !== null) {
      dispatch(finishEditingQuestion());
    }
    dispatch(changeStatusToOk());
  };

const changeQuestionOrder =
  ({
    questionId,
    order,
    quizQuestionOrderUrl,
  }: {
    questionId: string;
    order: number;
    quizQuestionOrderUrl: QuizQuestionOrderUrl;
  }): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      teachers: {
        quizData: { editQuiz },
      },
    } = state;
    const minOrder = 1;
    const maxOrder = editQuiz.questions.length;
    if (order < minOrder || order > maxOrder) {
      return;
    }

    const {
      teachers: {
        quizData: {
          editQuiz: { id: quizId },
        },
      },
    } = state;
    dispatch(changeStatusToSaving());
    const { orders: questionOrderMap } = await questionApi.moveQuestion({
      quizId: quizId as number,
      questionId,
      order: order,
      quizQuestionOrderUrl: quizQuestionOrderUrl,
    });
    dispatch(
      updateQuizQuestionOrder({
        questionOrderMap,
      })
    );
    dispatch(changeStatusToOk());
  };

const loadQuizBySocNumber =
  ({
    socNumber,
    quizUrl,
  }: {
    socNumber: string;
    quizUrl: ({ quizId }?: { quizId?: number }) => string;
  }): AppThunk =>
  async (dispatch) => {
    dispatch(changeStatusToLoading());
    const quiz = await quizApi.loadQuiz({
      socNumber: socNumber,
      quizUrl: quizUrl,
    });
    await dispatch(populateQuizData(quiz));
    dispatch(changeStatusToOk());
  };

const handleUnexpectedError =
  (error: unknown): AppThunk =>
  async (dispatch) => {
    dispatch(changeStatusToError({ error }));
    if (error instanceof Error) {
      const { message, stack, name } = error;
      const exception = new Error(message);
      exception.name = name;
      exception.stack = stack;
      captureException(exception);
    }
  };

const saveAndCreateNewQuestion =
  ({
    questionType,
    position,
    quizQuestionUrl,
    selectedQuestions,
    unselectQuestion,
    selectQuestion,
  }: {
    position: number;
    questionType: QuestionTypes;
    quizQuestionUrl: QuizQuestionUrl;
    selectQuestion: (questionId: string | number) => void;
    selectedQuestions: Set<string>;
    unselectQuestion: (questionId: string | number) => void;
  }): AppThunk =>
  async (dispatch, getState, extraArguments) => {
    const state = getState();
    const {
      teachers: {
        quizData: { editQuestion },
      },
    } = state;
    if (editQuestion) {
      await saveQuestion({
        quizQuestionUrl: quizQuestionUrl,
        selectQuestion: selectQuestion,
        selectedQuestions: selectedQuestions,
        unselectQuestion: unselectQuestion,
      })(dispatch, getState, extraArguments);
    }
    dispatch(
      createBlankQuestion({
        questionType,
        position,
      })
    );
  };

interface DeleteQuizParams {
  quizId: number | string;
  deleteQuizUrl: DeleteQuizUrl;
}
const deleteQuiz =
  ({ quizId, deleteQuizUrl }: DeleteQuizParams): AppThunk =>
  async (dispatch) => {
    try {
      await quizApi.deleteQuiz({
        quizId: quizId,
        deleteQuizUrl: deleteQuizUrl,
      });
    } catch (error: unknown) {
      await dispatch(handleUnexpectedError(error));
    }
    dispatch(finishEditingQuiz());
  };

export {
  loadQuizBySocNumber,
  populateQuizData,
  editQuestion,
  saveQuestion,
  saveQuiz,
  deleteQuestion,
  changeQuestionOrder,
  handleUnexpectedError,
  setQuestionAndAnswers,
  saveAndCreateNewQuestion,
  deleteQuiz,
};
